// src/mixins/SubscriptionAccessAppsyncMixin.js
//
// Teacher-side read model for the subscription system. Same shape/philosophy as
// TeacherProfileAppsyncMixin.js: one owner-scoped query on boot, then live
// AppSync subscriptions. No DataStore involvement at all.
//
// Access is keyed on the signed-in user's Cognito EMAIL, not on their
// TeacherProfile or their school - so two accounts sharing one email both get
// access, and a teacher with no School on their profile still gets access.
//
// From the access rows we derive, in order:
//   subscriptionAccessRecords    -> raw SubscriptionAccess rows for this email
//   subscriptionPackages         -> the SubscriptionPackage behind each row
//   subscriptionAccessSubjectIDs -> (getter) subjects the teacher can see
//   subscriptionAccessProductIds -> Product + ProductBundle ids for those
//                                   subjects, PLUS every product inside an
//                                   accessible bundle (mirrors how
//                                   syncedBoughtProducts expands bundles).
//
// That last array is what the product grids treat as "owned".
//
// Relies on the host component (App.vue) for `this.authMode`.

import { API, Auth } from "aws-amplify";
import store from "@/store";
import {
  subscriptionAccessesByEmail,
  getSubscriptionPackage,
} from "@/graphql/queries";
import {
  onCreateSubscriptionAccess,
  onUpdateSubscriptionAccess,
  onDeleteSubscriptionAccess,
  onUpdateSubscriptionPackage,
} from "@/graphql/subscriptions";
import { parseGraphqlResult } from "@/core/utils/parseGraphqlResult";
import { cleanEmail } from "@/core/utils/subscriptionAccess";

// Deliberately id-only (plus `products` on bundles so we can expand them).
// The grids render from DataStore, so we never need the full records here.
const productIdsBySubject = /* GraphQL */ `
  query ProductIdsBySubject($subjectID: ID!, $nextToken: String) {
    productsBySubject(
      subjectID: $subjectID
      limit: 1000
      nextToken: $nextToken
    ) {
      items {
        id
        _deleted
      }
      nextToken
    }
  }
`;

const productBundleIdsBySubject = /* GraphQL */ `
  query ProductBundleIdsBySubject($subjectID: ID!, $nextToken: String) {
    productBundlesBySubject(
      subjectID: $subjectID
      limit: 1000
      nextToken: $nextToken
    ) {
      items {
        id
        products
        _deleted
      }
      nextToken
    }
  }
`;

export default {
  data() {
    return {
      subscriptionAccessEmail: "",
      subscriptionAccessCreateSub: null,
      subscriptionAccessUpdateSub: null,
      subscriptionAccessDeleteSub: null,
      subscriptionPackageUpdateSub: null,
    };
  },

  methods: {
    // -----------------------------------------------------------------------
    // Entry point. Safe to call more than once.
    // -----------------------------------------------------------------------
    async syncSubscriptionAccess() {
      try {
        const user = await Auth.currentAuthenticatedUser().catch(() => {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
        });

        if (!user) {
          store.commit("storeSubscriptionAccessRecords", []);
          store.commit("storeSubscriptionPackages", []);
          store.commit("storeSubscriptionAccessProductIds", []);
          store.commit("storeSubscriptionAccessSynced", true);
          return;
        }

        // Use the email EXACTLY as Cognito holds it - owner auth compares the
        // raw `email` claim, so any normalising here would break the match.
        const email = cleanEmail(user.attributes?.email);
        if (!email) {
          store.commit("storeSubscriptionAccessSynced", true);
          return;
        }
        this.subscriptionAccessEmail = email;

        let allItems = [];
        let nextToken = null;

        do {
          const result = await API.graphql({
            query: subscriptionAccessesByEmail,
            variables: {
              email,
              filter: { _deleted: { ne: true } },
              nextToken, // null on first call
            },
            authMode: this.authMode,
          });

          const page = result.data.subscriptionAccessesByEmail;
          allItems = [...allItems, ...page.items];
          nextToken = page.nextToken;
        } while (nextToken);

        const records = parseGraphqlResult(allItems);
        store.commit("storeSubscriptionAccessRecords", records);

        await this.loadSubscriptionPackages(records);

        store.commit("storeSubscriptionAccessSynced", true);

        // Open live subs even when there is currently no access, so that an
        // admin granting access mid-session lands immediately.
        this.subscribeSubscriptionAccess(email);
      } catch (error) {
        console.log("syncSubscriptionAccess error:", error);
        store.commit("storeSubscriptionAccessSynced", true);
      }
    },

    // Fetch (and cache) the SubscriptionPackage behind each access row, then
    // resolve the products/bundles those packages unlock.
    async loadSubscriptionPackages(records) {
      try {
        const ids = [
          ...new Set(
            (records || [])
              .map((r) => r.subscriptionPackageID)
              .filter((id) => !!id),
          ),
        ];

        const cached = store.state.subscriptionPackages || [];
        const packages = [];

        for (const id of ids) {
          const hit = cached.find((p) => p.id === id);
          if (hit) {
            packages.push(hit);
            continue;
          }

          const result = await API.graphql({
            query: getSubscriptionPackage,
            variables: { id },
            authMode: this.authMode,
          }).catch((error) => {
            console.log("getSubscriptionPackage error:", id, error);
            return null;
          });

          const pkg = parseGraphqlResult(
            result?.data?.getSubscriptionPackage || null,
          );
          if (pkg && !pkg._deleted) packages.push(pkg);
        }

        store.commit("storeSubscriptionPackages", packages);

        await this.loadSubscriptionAccessProductIds();
      } catch (error) {
        console.log("loadSubscriptionPackages error:", error);
      }
    },

    // -----------------------------------------------------------------------
    // Subjects -> product/bundle ids. Reads the `subscriptionAccessSubjectIDs`
    // getter, so expired/inactive packages are already excluded.
    // -----------------------------------------------------------------------
    async loadSubscriptionAccessProductIds() {
      try {
        const subjectIDs = store.getters.subscriptionAccessSubjectIDs || [];
        console.log("loadSubscriptionAccessProductIds subjectIDs:", subjectIDs);

        if (subjectIDs.length === 0) {
          store.commit("storeSubscriptionAccessProductIds", []);
          return;
        }

        const ids = new Set();

        await Promise.all(
          subjectIDs.map(async (subjectID) => {
            // Products
            let nextToken = null;
            do {
              const result = await API.graphql({
                query: productIdsBySubject,
                variables: { subjectID, nextToken },
                authMode: this.authMode,
              }).catch((error) => {
                console.log("productsBySubject error:", subjectID, error);
                return null;
              });

              const page = result?.data?.productsBySubject;
              (page?.items || []).forEach((item) => {
                if (item && !item._deleted) ids.add(item.id);
              });
              nextToken = page?.nextToken || null;
            } while (nextToken);

            // Bundles - the bundle itself AND everything inside it, so that
            // opening a bundle product behaves exactly like a bought bundle.
            nextToken = null;
            do {
              const result = await API.graphql({
                query: productBundleIdsBySubject,
                variables: { subjectID, nextToken },
                authMode: this.authMode,
              }).catch((error) => {
                console.log("productBundlesBySubject error:", subjectID, error);
                return null;
              });

              const page = result?.data?.productBundlesBySubject;
              (page?.items || []).forEach((item) => {
                if (!item || item._deleted) return;
                ids.add(item.id);

                let inner = item.products;
                if (typeof inner === "string") {
                  try {
                    inner = JSON.parse(inner);
                  } catch (e) {
                    inner = null;
                  }
                }
                if (Array.isArray(inner)) {
                  inner.forEach((p) => {
                    if (p?.id) ids.add(p.id);
                  });
                }
              });
              nextToken = page?.nextToken || null;
            } while (nextToken);
          }),
        );

        store.commit("storeSubscriptionAccessProductIds", [...ids]);
      } catch (error) {
        console.log("loadSubscriptionAccessProductIds error:", error);
      }
    },

    subscribeSubscriptionAccess(email) {
      if (this.subscriptionAccessUpdateSub) return; // already subscribed

      // NOTE: because SubscriptionAccess has an owner rule on `email`, the
      // generated subscriptions take an `email` argument that must equal the
      // token's email claim - otherwise AppSync rejects the connection.
      this.subscriptionAccessCreateSub = API.graphql({
        query: onCreateSubscriptionAccess,
        variables: { email },
        authMode: this.authMode,
      }).subscribe({
        next: ({ value }) => {
          const created = parseGraphqlResult(
            value.data.onCreateSubscriptionAccess,
          );
          if (created) this.upsertSubscriptionAccessRecord(created);
        },
        error: (error) =>
          console.log("onCreateSubscriptionAccess subscription error:", error),
      });

      this.subscriptionAccessUpdateSub = API.graphql({
        query: onUpdateSubscriptionAccess,
        variables: { email, filter: { _deleted: { ne: true } } },
        authMode: this.authMode,
      }).subscribe({
        next: ({ value }) => {
          const updated = parseGraphqlResult(
            value.data.onUpdateSubscriptionAccess,
          );
          if (!updated) return;
          if (updated._deleted) this.removeSubscriptionAccessRecord(updated.id);
          else this.upsertSubscriptionAccessRecord(updated);
        },
        error: (error) =>
          console.log("onUpdateSubscriptionAccess subscription error:", error),
      });

      this.subscriptionAccessDeleteSub = API.graphql({
        query: onDeleteSubscriptionAccess,
        variables: { email },
        authMode: this.authMode,
      }).subscribe({
        next: ({ value }) => {
          const deleted = value.data.onDeleteSubscriptionAccess;
          if (deleted) this.removeSubscriptionAccessRecord(deleted.id);
        },
        error: (error) =>
          console.log("onDeleteSubscriptionAccess subscription error:", error),
      });

      // Package-level changes (admin deactivates / changes subjects / expiry).
      // SubscriptionPackage is `allow: private read`, so no owner arg here; we
      // just ignore anything we don't hold access to.
      this.subscriptionPackageUpdateSub = API.graphql({
        query: onUpdateSubscriptionPackage,
        authMode: this.authMode,
      }).subscribe({
        next: async ({ value }) => {
          const updated = parseGraphqlResult(
            value.data.onUpdateSubscriptionPackage,
          );
          if (!updated) return;

          const records = store.state.subscriptionAccessRecords || [];
          if (!records.some((r) => r.subscriptionPackageID === updated.id))
            return;

          const current = store.state.subscriptionPackages || [];
          const next = updated._deleted
            ? current.filter((p) => p.id !== updated.id)
            : current.some((p) => p.id === updated.id)
              ? current.map((p) => (p.id === updated.id ? updated : p))
              : [...current, updated];

          store.commit("storeSubscriptionPackages", next);

          // Subjects on the package may have changed -> re-resolve products.
          await this.loadSubscriptionAccessProductIds();
        },
        error: (error) =>
          console.log("onUpdateSubscriptionPackage subscription error:", error),
      });
    },

    async upsertSubscriptionAccessRecord(record) {
      if (!record) return;
      const current = store.state.subscriptionAccessRecords || [];
      const next = current.some((r) => r.id === record.id)
        ? current.map((r) => (r.id === record.id ? record : r))
        : [...current, record];

      store.commit("storeSubscriptionAccessRecords", next);
      await this.loadSubscriptionPackages(next);
    },

    removeSubscriptionAccessRecord(id) {
      const next = (store.state.subscriptionAccessRecords || []).filter(
        (r) => r.id !== id,
      );
      store.commit("storeSubscriptionAccessRecords", next);
      this.loadSubscriptionPackages(next);
    },
  },

  beforeDestroy() {
    if (this.subscriptionAccessCreateSub)
      this.subscriptionAccessCreateSub.unsubscribe();
    if (this.subscriptionAccessUpdateSub)
      this.subscriptionAccessUpdateSub.unsubscribe();
    if (this.subscriptionAccessDeleteSub)
      this.subscriptionAccessDeleteSub.unsubscribe();
    if (this.subscriptionPackageUpdateSub)
      this.subscriptionPackageUpdateSub.unsubscribe();
  },
};
